import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot, Router } from '@angular/router';
import { take } from 'rxjs/operators';
import { User } from 'src/app/core/models/user/user.model';
import { AuthenticationService } from '../authentication/authentication.service';
import { MessagingService } from 'src/app/core/services/messaging.service';
import { Platform } from '@ionic/angular';
import { lastValueFrom } from 'rxjs';
import { CurrentUserStoreService } from '../services/current-user-store.service';
import { DEMO_MODE } from 'src/environments/environment';
import { SoftwarePermissionCode } from '../models/permissions/permission-code';

@Injectable({
    providedIn: 'root'
})

export class UserPermissionGuard  {

    constructor(private currentUserStoreService: CurrentUserStoreService,
                private authService: AuthenticationService,
                private messagingService: MessagingService,
                private router: Router,
                public platform: Platform) {}

    async canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> {
        if(DEMO_MODE) {
          return true;
        }
        let user = this.currentUserStoreService.getUser();
        if (user === undefined) {
            if (this.authService.isValid()) {
                await lastValueFrom(this.authService.getUserByToken()).then(u => {user = u; }).catch(err => {});
            } else {
                if (!this.authService.getAccessToken() && !this.authService.getRefreshToken()) {
                    this.messagingService.setMessage('Access denied. Please login')
                    return false;
                }
                if (!this.authService.refreshTokenInProgress) {
                    this.authService.setRefreshTokenInProgress(true);
                    await lastValueFrom(this.authService.refreshAccessToken()).then(r => user = r.user).catch(() => {});
                    this.authService.setRefreshTokenInProgress(false);
                }
                await lastValueFrom(this.authService.refreshChanged.pipe(take(2))).then(val => {
                    user = this.currentUserStoreService.getUser()}).catch(err => {});
            }
            return this.validate(user, state);
        } else {
            return this.validate(user, state);
        }
    }

    validate(user: User, state: RouterStateSnapshot) {
        const splitedUrl = state.url.split('/');
        splitedUrl.shift();
        if (this.userContains(user, splitedUrl)) {
            return true;
        } else {
            this.router.navigateByUrl('/restricted-access');
            return false;
        }
    }

    userContains(user: User, wantedRoute: string[]) {
        if (User.isAdria(user)) {
          return true;
        }
        let requiredPermissionCode: string[];
        switch (wantedRoute[0]) {
            case 'rooms': {
              return true;
            }
            case 'common-area' : {
              return true;
            }
            case 'groups' : {
              requiredPermissionCode = [
                SoftwarePermissionCode.LocationGroupsActivatePresets,
                SoftwarePermissionCode.LocationGroupsUpdate];
              break;
            }
            case 'cards': {
              requiredPermissionCode = [
                SoftwarePermissionCode.CardsGuestUpdate,
                SoftwarePermissionCode.CardsStaffUpdate];
              break;
            }
            /* case 'automation': {
              requiredPermissionCode = SoftwarePermissionCode.AutomationsView;
              break;
            } */
            case 'schedule' : {
              requiredPermissionCode = [SoftwarePermissionCode.AutomationsView];
              break;
            }
            case 'logs': {
              if (wantedRoute.length === 2 && wantedRoute[1] === 'audit') {
                requiredPermissionCode = [SoftwarePermissionCode.AuditLogView];
              } else if (wantedRoute.length === 2 && wantedRoute[1] === 'access') {
                requiredPermissionCode = [SoftwarePermissionCode.AccessLogView];
              } else if (wantedRoute.length === 2 && wantedRoute[1] === 'alarms') {
                requiredPermissionCode = [SoftwarePermissionCode.AlarmsLogView];
              } else if (wantedRoute.length === 2 && wantedRoute[1] === 'card-events') {
                requiredPermissionCode = [SoftwarePermissionCode.SettingsCardEventLog];
              }
              break;
            }
            case 'users': {
                requiredPermissionCode = [SoftwarePermissionCode.UsersView];
                if (+wantedRoute[1] === user.userId && wantedRoute.length === 2) { // logged in user profile
                    return true;
                } else if (+wantedRoute[1] === user.userId && wantedRoute[2] === 'permissions') { // logged in user own permission
                    return false;
                } else if (wantedRoute[2] === 'permissions') {
                  requiredPermissionCode = [SoftwarePermissionCode.UsersView];
                } else if (+wantedRoute[1] === 0) {
                  requiredPermissionCode = [SoftwarePermissionCode.UsersUpdate];
                } else if (wantedRoute[2] === 'cards') {
                  requiredPermissionCode = [ SoftwarePermissionCode.CardsStaffUpdate];
                }
                if (wantedRoute.length === 6 && wantedRoute[4] === 'rules') {
                  requiredPermissionCode = [SoftwarePermissionCode.UsersUpdate];
                }
                if (wantedRoute.length === 4 && wantedRoute[3] === 'permissions') {
                  requiredPermissionCode = [SoftwarePermissionCode.UsersUpdate];
                }
                break;
            }
            case 'alarms': {
                requiredPermissionCode = [
                  SoftwarePermissionCode.AlarmsRoomBurglaryReceive,
                  SoftwarePermissionCode.AlarmsSosReceive,
                  SoftwarePermissionCode.AlarmsRoomBurglaryReceive,
                  SoftwarePermissionCode.AlarmsRoomSafeBurglaryReceive,
                  SoftwarePermissionCode.AlarmsTemperatureDifferenceReceive,
                  SoftwarePermissionCode.AlarmsWaterFloodReceive,
                  SoftwarePermissionCode.AlarmsPowerOutageReceive,
                  SoftwarePermissionCode.AlarmsDCOutputsFailureReceive,
                  SoftwarePermissionCode.AlarmsControllerTemperatureReceive,
                  SoftwarePermissionCode.AlarmsWindowOpenTooLongReceive,
                  SoftwarePermissionCode.AlarmsDoorOpenTooLongReceive,
                  SoftwarePermissionCode.AlarmsRoomSafeCommErrorReceive,
                  SoftwarePermissionCode.AlarmsCommunicationErrorReceive
                ]
                // if (wantedRoute.length === 2 && wantedRoute[1] === 'unconfirmed') {
                //     requiredPermissionCode = SoftwarePermissionCode.AlarmsReceive;
                // }
                break;
            }
            case 'roles': {
                requiredPermissionCode = [SoftwarePermissionCode.RolesView];
                if (wantedRoute.length === 2 && wantedRoute[1] === '0') {
                    requiredPermissionCode = [SoftwarePermissionCode.RolesUpdate];
                }
                else if (wantedRoute.length === 2 && typeof (+wantedRoute[1]) === 'number') {
                    requiredPermissionCode = [SoftwarePermissionCode.RolesView];
                }
                break;
            }
            case 'settings': {
              requiredPermissionCode = [SoftwarePermissionCode.Settings];
              if (wantedRoute.length === 2 && wantedRoute[1] === 'views') {
                requiredPermissionCode = [SoftwarePermissionCode.SettingsManageRoomOverview];
              } else if (wantedRoute.length === 2 && wantedRoute[1] === 'other-settings') {
                requiredPermissionCode = [SoftwarePermissionCode.SettingsDefaults];
              } else if (wantedRoute.length === 2 && wantedRoute[1] === 'encoders') {
              requiredPermissionCode = [SoftwarePermissionCode.SettingsEncoders];
              } else if (wantedRoute.length === 2 && wantedRoute[1] === 'presets') {
                requiredPermissionCode = [SoftwarePermissionCode.SettingsPresets];
              } else if (wantedRoute.length === 2 && wantedRoute[1] === 'hvac-mode-cross-reference') {
                requiredPermissionCode = [SoftwarePermissionCode.SettingsPresets];
              } else if (wantedRoute.length === 2 && wantedRoute[1] === 'alert-subscriptions') {
                requiredPermissionCode = [SoftwarePermissionCode.SettingsSubscription];
              } else if (wantedRoute.length === 2 && wantedRoute[1] === 'licensing') {
                requiredPermissionCode = [SoftwarePermissionCode.SettingsLicensing];
              } else if (wantedRoute.length === 2 && wantedRoute[1] === 'analytics') {
                requiredPermissionCode = [SoftwarePermissionCode.AnalyticsView];
              } else if (wantedRoute.length === 2 && wantedRoute[1] === 'access-control-rules') {
                requiredPermissionCode = [SoftwarePermissionCode.AutomationsView];
              } else if (wantedRoute.length === 2 && wantedRoute[1] === 'external-users') {
                requiredPermissionCode = [SoftwarePermissionCode.UsersView];
              } else if (wantedRoute.length === 2 && wantedRoute[1] === 'project-details') {
                return User.isAdria(user);
              } else if (wantedRoute.length === 2 && wantedRoute[1] === 'controller-settings') {
                return User.isAdria(user);
              } else if (wantedRoute.length === 2 && wantedRoute[1] === 'controllers-overview') {
                return User.isAdria(user);
              } else if (wantedRoute.length === 2 && wantedRoute[1] === 'pms-options') {
                return User.isAdria(user);
              } else if (wantedRoute.length === 2 && wantedRoute[1] === 'drivers') {
                return User.isAdria(user);
              } else if (wantedRoute.length === 2 && wantedRoute[1] === 'hide-properties') {
                return User.isAdria(user);
              }
                else if (wantedRoute.length === 2 && wantedRoute[1] === 'auto-confirm-alarm') {
                  return User.isAdria(user);
              }
                break;
            }
          case 'hvac-mode' : {
            requiredPermissionCode = [SoftwarePermissionCode.LocationGroupsActivatePresets];
            break;
        }

            case 'how-to' : {
              return true;
            }
            default: {
                return false;
            }
        }
        /* userHasPermission = user?.roles?.some(role => role?.permissions?.some(permission => {
          if (requiredPermissionCode === SoftwarePermissionCode.AlarmsRoomBurglaryReceive) {
            return this.permissionIsAnyAlarm(permission.permissionId);
          } else {
            return permission.permissionId === requiredPermissionCode;
          }
          }
        ));
        if (!userHasPermission) {
          userHasPermission = user?.permissions?.some(permission => {
            if (requiredPermissionCode === SoftwarePermissionCode.AlarmsRoomBurglaryReceive) {
              return this.permissionIsAnyAlarm(permission.permissionId);
            } else {
              return permission.permissionId === requiredPermissionCode;
            }
           });
        } */
        return User.hasAnyOfRequiredPermissions(user, requiredPermissionCode);
    }


   /*  permissionIsAnyAlarm(permissionId: number): boolean {
      return permissionId === SoftwarePermissionCode.AlarmsSosReceive ||
      permissionId === SoftwarePermissionCode.AlarmsRoomBurglaryReceive ||
      permissionId === SoftwarePermissionCode.AlarmsRoomSafeBurglaryReceive ||
      permissionId === SoftwarePermissionCode.AlarmsTemperatureDifferenceReceive ||
      permissionId === SoftwarePermissionCode.AlarmsWaterFloodReceive ||
      permissionId === SoftwarePermissionCode.AlarmsPowerOutageReceive ||
      permissionId === SoftwarePermissionCode.AlarmsDCOutputsFailureReceive ||
      permissionId === SoftwarePermissionCode.AlarmsControllerTemperatureReceive ||
      permissionId === SoftwarePermissionCode.AlarmsWindowOpenTooLongReceive ||
      permissionId === SoftwarePermissionCode.AlarmsDoorOpenTooLongReceive ||
      permissionId === SoftwarePermissionCode.AlarmsRoomSafeCommErrorReceive ||
      permissionId === SoftwarePermissionCode.AlarmsCommunicationErrorReceive;
    } */
}
